import { useReducer, useMemo } from 'react'
import {
    EncoderState,
    EncoderEvent,
    SuggestedChange,
    EncoderStatus,
} from '../components/Reader/Encoder/types'
import {
    encoderReducer,
    createInitialState,
    getSubmitterId,
} from '../components/Reader/Encoder/EncoderStateMachine'
import { Roles } from '../types/Roles'
import { RoleType } from '../contexts/Auth'

interface UseEncoderStateMachineResult {
    state: EncoderState
    dispatch: React.Dispatch<EncoderEvent>
    // Computed properties for easier consumption
    isLoading: boolean
    canSubmit: boolean
    canUpdate: boolean
    canApprove: boolean
    canCancel: boolean
    canUndo: boolean
    showDiffPreview: boolean
    isOwner: boolean
}

export function useEncoderStateMachine(
    originalBody: string,
    suggestedChange: SuggestedChange | null,
    userId: string | undefined,
    role: RoleType,
    hasFormikErrors: boolean
): UseEncoderStateMachineResult {
    const [state, dispatch] = useReducer(
        encoderReducer,
        { originalBody, suggestedChange },
        ({ originalBody, suggestedChange }) =>
            createInitialState(originalBody, suggestedChange)
    )

    const computed = useMemo(() => {
        const loadingStates: EncoderStatus[] = [
            'SUBMITTING',
            'UPDATING',
            'APPROVING',
            'CANCELLING',
        ]
        const isLoading = loadingStates.includes(state.status)

        const submitterId = getSubmitterId(state.submittedBy)
        const isOwner = userId !== undefined && submitterId === userId
        const isAdmin = role === Roles.Admin

        const hasErrors =
            hasFormikErrors || state.serverValidationErrors.length > 0

        // Can submit: editing/draft_saved, no errors, has changes
        const canSubmit =
            (state.status === 'EDITING' || state.status === 'DRAFT_SAVED') &&
            !hasErrors &&
            state.currentValue !== state.originalValue

        // Can update: pending_approval, owner, has changes from submission, no errors
        const canUpdate =
            state.status === 'PENDING_APPROVAL' &&
            isOwner &&
            state.hasUnsavedChanges &&
            !hasErrors

        // Can approve: pending_approval, admin
        const canApprove = state.status === 'PENDING_APPROVAL' && isAdmin

        // Can cancel: pending_approval, owner or admin
        const canCancel =
            state.status === 'PENDING_APPROVAL' && (isOwner || isAdmin)

        // Can undo: editing or draft_saved
        const canUndo =
            state.status === 'EDITING' || state.status === 'DRAFT_SAVED'

        // Show diff preview when there are changes
        const showDiffPreview = state.currentValue !== state.originalValue

        return {
            isLoading,
            canSubmit,
            canUpdate,
            canApprove,
            canCancel,
            canUndo,
            showDiffPreview,
            isOwner,
        }
    }, [
        state.status,
        state.submittedBy,
        state.hasUnsavedChanges,
        state.currentValue,
        state.originalValue,
        state.serverValidationErrors.length,
        userId,
        role,
        hasFormikErrors,
    ])

    return {
        state,
        dispatch,
        ...computed,
    }
}
